import { CnpjInput } from '@/_components/inputs/cnpj-input'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from '@/_components/ui'
import { ContentContainer } from '@/features/_components/content-container'
import { InputGroup } from '@/features/_components/input-group'
import { Text } from '@/features/_components/ui/typography'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import type { TExposedCompanyDTO, TUpdateCompanyInputDTO } from '@doutorsim/shared/companies'
import { Title } from '@radix-ui/react-dialog'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Pencil, X } from 'lucide-react'
import { useContext, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { CiCircleAlert } from 'react-icons/ci'
import { toast } from 'sonner'
import { EditContext } from '../_providers/edit'

type CompanySettingsFormProps = {
  company: TExposedCompanyDTO
  canEdit?: boolean
}

export const CompanySettingsForm = ({ company, canEdit = false }: CompanySettingsFormProps) => {
  const queryClient = useQueryClient()
  const updateCompany = useMutation(mutations.companies.update(queryClient))

  const isAdmin = canEdit

  const form = useFormContext<TUpdateCompanyInputDTO>()
  const { isFormEditable, isCancelDialogOpen, setIsCancelDialogOpen, setIsFormEditable } =
    useContext(EditContext)

  const formWasModified = Object.keys(form.formState.dirtyFields).length > 0

  const handleCancelClick = () => setIsCancelDialogOpen(true)

  const formRef = useRef<HTMLFormElement | null>(null)

  const buttonDisabledStates = !formWasModified || form.formState.isSubmitting || !isFormEditable

  const resetForm = () => {
    form.reset()
    setIsCancelDialogOpen(false)
    setIsFormEditable(false)
  }

  const onSubmit = (formData: TUpdateCompanyInputDTO) => {
    updateCompany.mutate(
      {
        id: company.id,
        name: formData.name,
        legalName: formData.legalName,
        cnpj: formData.cnpj,
        franchiseId: null,
        requiresHandoff: formData.requiresHandoff,
      },
      {
        onSuccess: () => {
          setIsFormEditable(false)
          formRef.current?.scrollIntoView({ behavior: 'smooth' })
          form.reset(formData)
          toast.success('Instituição atualizada com sucesso!')
        },
        onError: (error) => {
          console.error(error)
          toast.error('Erro ao atualizar a instituição', {
            description:
              error instanceof ApiQueryError
                ? (error.apiError.detail ?? error.message)
                : 'Erro inesperado',
          })
        },
      },
    )
  }

  return (
    <div className="w-full overflow-auto">
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent className="min-w-[336px] max-w-[389px] p-5">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-1">
                <CiCircleAlert size={24} />
                <Title className="text-base">Há alterações não salvas!</Title>
              </div>
              <Text>Ao prosseguir, essas alterações serão perdidas. Deseja continuar?</Text>
            </div>

            <div className="flex gap-3 self-end">
              <DialogClose asChild>
                <Button variant="gray" className="rounded-md font-bold">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                className="min-w-[80px] text-sm font-bold"
                onClick={resetForm}
              >
                Continuar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Form {...form}>
        <form
          className="flex w-full flex-col gap-4 pb-2"
          onSubmit={form.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <ContentContainer>
            <div className="flex items-center justify-between">
              <h2 className="font-bold">Identificação</h2>
              {isAdmin && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    if (isFormEditable && formWasModified) {
                      return setIsCancelDialogOpen(true)
                    }
                    setIsFormEditable(!isFormEditable)
                  }}
                >
                  {isFormEditable ? (
                    <>
                      <X size={12} />
                      Cancelar
                    </>
                  ) : (
                    <>
                      <Pencil size={12} />
                      Editar
                    </>
                  )}
                </button>
              )}
            </div>
            <InputGroup>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Nome fantasia</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite o nome fantasia"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="legalName"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">Razão Social</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Digite a razão social"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="text-sm font-semibold">CNPJ</FormLabel>
                    <FormControl>
                      <CnpjInput
                        placeholder="Digite o CNPJ da instituição"
                        disabled={!isAdmin || !isFormEditable}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
          </ContentContainer>

          <ContentContainer>
            <h2 className="font-bold">Fluxo de aprovação</h2>
            <FormField
              control={form.control}
              name="requiresHandoff"
              render={({ field }) => (
                <FormItem className="flex w-full flex-row items-center justify-between gap-6 space-y-0">
                  <div className="flex flex-col gap-1">
                    <FormLabel className="text-sm font-semibold">
                      Liberação manual de propostas
                    </FormLabel>
                    <p className="text-sm text-brand-grayscale-7">
                      Quando ativo, solicitações aprovadas ficam em &quot;Contatar paciente&quot; e
                      a proposta só é enviada por WhatsApp após a liberação pela instituição.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                      disabled={!isAdmin || !isFormEditable}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </ContentContainer>
          <p className="flex items-center h-1 px-5 text-sm opacity-50">
            Criada em{' '}
            {company.createdAt
              ? new Date(company.createdAt).toLocaleDateString('pt-BR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : '-'}
          </p>
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                variant="gray"
                type="button"
                disabled={buttonDisabledStates}
                onClick={handleCancelClick}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={buttonDisabledStates}>
                {updateCompany.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}
